import { Minus, Plus, Users } from 'lucide-react';
import { DynamicIcon } from 'lucide-react/dynamic';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, ProgressBar, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ContributionInfo } from '../../service/api';
import api from '../../service/api';
import useShopItemsStore from '../../store/shopItems';
import useUserStore from '../../store/user';
import Coin from '../Coin';

interface CollectableShopItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemId: string;
}

/**
 * Modal for collectable shop items where group members can pool stickers
 * toward a shared purchase. Shows progress bar, contributor list, and
 * contribution input.
 */
const CollectableShopItemModal: React.FC<CollectableShopItemModalProps> = ({
  isOpen,
  onClose,
  itemId,
}) => {
  const [amount, setAmount] = useState(1);
  const [isContributing, setIsContributing] = useState(false);
  const [isLoadingContributions, setIsLoadingContributions] = useState(false);
  const [contributionInfo, setContributionInfo] =
    useState<ContributionInfo | null>(null);
  const { t } = useTranslation();

  const getShopItemById = useShopItemsStore(state => state.getShopItemById);
  const fetchShopItems = useShopItemsStore(state => state.fetchShopItems);
  const balance = useUserStore(state => state.balance);
  const updateBalance = useUserStore(state => state.updateBalance);

  const item = getShopItemById(itemId);

  const loadContributions = useCallback(async () => {
    setIsLoadingContributions(true);
    try {
      const info = await api.shopGetContributions(itemId);
      setContributionInfo(info);
    } catch (error) {
      console.error('Failed to load contributions:', error);
    } finally {
      setIsLoadingContributions(false);
    }
  }, [itemId]);

  useEffect(() => {
    if (isOpen && itemId) {
      loadContributions();
    }
  }, [isOpen, itemId, loadContributions]);

  const { maxContribution, progressPercent, isMinusEnabled, isPlusEnabled } =
    useMemo(() => {
      if (!item || !contributionInfo) {
        return {
          maxContribution: 0,
          progressPercent: 0,
          isMinusEnabled: false,
          isPlusEnabled: false,
        };
      }

      const remaining = contributionInfo.remaining;
      const max = Math.min(remaining, balance);
      const percent =
        item.price > 0
          ? Math.round((contributionInfo.totalCollected / item.price) * 100)
          : 0;

      return {
        maxContribution: max,
        progressPercent: Math.min(percent, 100),
        isMinusEnabled: amount > 1,
        isPlusEnabled: amount < max,
      };
    }, [item, contributionInfo, balance, amount]);

  // Reset amount when max changes
  useEffect(() => {
    if (maxContribution > 0) {
      setAmount(prev => Math.min(prev, maxContribution));
    } else {
      setAmount(1);
    }
  }, [maxContribution]);

  if (!item || !isOpen) return null;

  const { title, description, icon, price } = item;

  const handleAmountChange = (delta: number) => {
    setAmount(prev => Math.max(1, Math.min(prev + delta, maxContribution)));
  };

  const handleContribute = async () => {
    setIsContributing(true);
    try {
      await api.shopContribute(itemId, amount);
      await Promise.all([
        fetchShopItems(),
        updateBalance(),
        loadContributions(),
      ]);
      setAmount(1);
    } catch (error) {
      console.error('Failed to contribute:', error);
    } finally {
      setIsContributing(false);
    }
  };

  const handleClose = () => {
    if (!isContributing) {
      setAmount(1);
      setContributionInfo(null);
      onClose();
    }
  };

  const isFullyFunded = contributionInfo?.isFullyFunded ?? false;
  const canContribute = maxContribution > 0 && !isFullyFunded;

  return (
    <Modal
      show={isOpen}
      onHide={handleClose}
      centered
      size="lg"
      className="fs-3"
      contentClassName="position-relative"
      backdrop="static"
    >
      <Modal.Dialog
        className="m-0"
        style={{
          backgroundColor: 'rgba(6, 56, 96, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        {/* Floating icon */}
        <div
          className="position-absolute top-0 start-50 translate-middle d-flex align-items-center justify-content-center rounded-circle bg-primary"
          style={{
            width: '64px',
            height: '64px',
            border: '4px solid rgba(6, 56, 96, 0.95)',
            zIndex: 1,
          }}
        >
          <DynamicIcon
            name={icon}
            size={32}
            strokeWidth={1.5}
            className="text-white"
          />
        </div>

        <Modal.Header
          closeButton={!isContributing}
          closeVariant="white"
          className="border-0 pt-5 pb-2"
          style={{ backgroundColor: 'transparent' }}
        >
          <Modal.Title className="fs-1 text-white fw-bold w-100 text-center">
            {title}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          className="text-white px-4 pb-4"
          style={{ backgroundColor: 'transparent' }}
        >
          {/* Collectable badge */}
          <div className="text-center mb-3">
            <span className="badge bg-info fs-5">
              <Users size={16} className="me-1" />
              {t('shop.collectable')}
            </span>
          </div>

          {description && (
            <div className="mb-3">
              <h6 className="text-white-50 mb-2">{t('common.description')}</h6>
              <p className="mb-0">{description}</p>
            </div>
          )}

          <p className="text-white-50 text-center small mb-3">
            {t('shop.collectableDescription')}
          </p>

          {/* Price display */}
          <div className="text-center mb-3">
            <Coin size="lg">{price}</Coin>
          </div>

          {/* Progress section */}
          {isLoadingContributions ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
            </div>
          ) : contributionInfo ? (
            <>
              {/* Progress bar */}
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-white-50">
                    {t('shop.collected')}:{' '}
                    <strong className="text-white">
                      {contributionInfo.totalCollected}
                    </strong>
                  </span>
                  <span className="text-white-50">
                    {t('shop.remaining')}:{' '}
                    <strong className="text-white">
                      {contributionInfo.remaining}
                    </strong>
                  </span>
                </div>
                <ProgressBar
                  now={progressPercent}
                  variant={isFullyFunded ? 'success' : 'info'}
                  animated={!isFullyFunded}
                  label={`${progressPercent}%`}
                  style={{ height: '1.5rem', fontSize: '0.9rem' }}
                />
                {isFullyFunded && (
                  <div className="text-center text-success fw-bold mt-2 fs-4">
                    🎉 {t('shop.fullyFunded')}
                  </div>
                )}
              </div>

              {/* Contributors list */}
              {contributionInfo.contributions.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-white-50 mb-2">
                    {t('shop.contributors')}
                  </h6>
                  <div
                    className="d-flex flex-column gap-2"
                    style={{ maxHeight: '150px', overflowY: 'auto' }}
                  >
                    {contributionInfo.contributions.map(contribution => (
                      <div
                        key={contribution._id}
                        className="d-flex justify-content-between align-items-center px-3 py-2 rounded"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        }}
                      >
                        <span>
                          {contribution.contributor.given_name}{' '}
                          {contribution.contributor.family_name}
                        </span>
                        <span className="d-flex align-items-center gap-1">
                          <strong>{contribution.amount}</strong>
                          <Coin size="xs" />
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {contributionInfo.contributions.length === 0 && (
                <p className="text-center text-white-50 mb-4">
                  {t('shop.noContributionsYet')}
                </p>
              )}

              {/* Contribution input */}
              {canContribute && (
                <>
                  <div className="mb-4">
                    <h6 className="text-white-50 mb-2 text-center">
                      {t('shop.contributeAmount')}
                    </h6>
                    <small className="d-block text-center text-white-50 mb-3">
                      {t('shop.yourMaxContribution', {
                        max: maxContribution,
                      })}
                    </small>
                    <div className="d-flex align-items-center justify-content-center gap-3">
                      <Button
                        variant="outline-light"
                        size="lg"
                        className="rounded-circle"
                        style={{ width: '64px', height: '64px' }}
                        onClick={() => handleAmountChange(-1)}
                        disabled={!isMinusEnabled || isContributing}
                      >
                        <Minus size={24} />
                      </Button>

                      <div
                        className="fs-1 fw-bold text-center"
                        style={{ minWidth: '80px' }}
                      >
                        {amount}
                      </div>

                      <Button
                        variant="outline-light"
                        size="lg"
                        className="rounded-circle"
                        style={{ width: '64px', height: '64px' }}
                        onClick={() => handleAmountChange(1)}
                        disabled={!isPlusEnabled || isContributing}
                      >
                        <Plus size={24} />
                      </Button>
                    </div>
                  </div>

                  <div className="text-center">
                    <Button
                      variant="success"
                      size="lg"
                      className="px-5 py-3 fs-3 fw-bold"
                      onClick={handleContribute}
                      disabled={isContributing || amount < 1}
                      style={{ minWidth: '200px' }}
                    >
                      {isContributing ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                            role="status"
                          />
                          {t('shop.contributing')}
                        </>
                      ) : (
                        <>
                          <Users size={28} className="me-2" />
                          {t('shop.contribute')}
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}

              {/* Cannot contribute - insufficient funds */}
              {!canContribute && !isFullyFunded && (
                <div className="text-center text-danger mb-3">
                  <small>{t('shop.insufficientFunds', { balance })}</small>
                </div>
              )}
            </>
          ) : null}
        </Modal.Body>
      </Modal.Dialog>
    </Modal>
  );
};

export default CollectableShopItemModal;
export type { CollectableShopItemModalProps };
